"use client";

import * as React from "react";
import { cn } from "../../shadcn/lib/utils";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../../shadcn/components/ui/navigation-menu";
import ArrowInclinedSvg from "../../icons/ArrowInclinedSvg";
import RoundCheckSvg from "../../icons/RoundCheckSvg";
import CallIcon from "../../icons/CallIcon";
import { Context, getSearchResults } from "services";

interface NavigationItems {
  title: string;
  value: string;
  children?: { title: string; href: string; value: string }[];
}

const navigationItemsFallback: NavigationItems[] = [
  {
    title: "Residential",
    value: "residential",
    children: [
      {
        title: "Flats",
        href: "/residential-flat-auction-properties",
        value: "flat",
      },
      {
        title: "Individual House",
        href: "/residential-individual-house-auction-properties",
        value: "individual-house",
      },
      {
        title: "Bungalow",
        href: "/residential-bungalow-auction-properties",
        value: "bungalow",
      },
      {
        title: "Farm house",
        href: "/residential-farm-house-auction-properties",
        value: "farm-house",
      },
      {
        title: "Villa",
        href: "/residential-villa-auction-properties",
        value: "villa",
      },
    ],
  },
  {
    title: "Commercial",
    value: "commercial",
    children: [
      {
        title: "Plot commercial",
        href: "/commercial-plot-commercial-auction-properties",
        value: "plot-commercial",
      },
      {
        title: "Mall",
        href: "/commercial-malls-auction-properties",
        value: "malls",
      },
      {
        title: "Office",
        href: "/commercial-office-auction-properties",
        value: "office",
      },
      {
        title: "Shops",
        href: "/commercial-shops-auction-properties",
        value: "shops",
      },
      {
        title: "Others",
        href: "/commercial-auction-properties",
        value: "others",
      },
    ],
  },
  {
    title: "Industrial",
    value: "industrial",
    children: [
      {
        title: "Godown",
        href: "/industrial-godowns-auction-properties",
        value: "godown",
      },
      {
        title: "Plant & machinery",
        href: "/industrial-auction-properties",
        value: "plant-machinery",
      },
      {
        title: "Goods",
        href: "/industrial-goods-auction-properties",
        value: "goods",
      },
      {
        title: "Plot industrial",
        href: "/industrial-plot-auction-properties",
        value: "plot-industrial",
      },
      {
        title: "Shed with plant & machinery",
        href: "/industrial-auction-properties",
        value: "shed-with-plant-machinery",
      },
      {
        title: "Industrial shed",
        href: "/industrial-auction-properties",
        value: "industrial-shed",
      },
      {
        title: "Industrial land",
        href: "/industrial-auction-properties",
        value: "industrial-land",
      },
    ],
  },
  {
    title: "Others",
    value: "others",
  },
];

export function NavigationTabs(props: any) {
  const { searchForm }: any = React.useContext(Context);
  const [activeItem, setActiveItem] = React.useState("residential");
  const [navigationItems, setNavigationItems] = React.useState<
    NavigationItems[]
  >(navigationItemsFallback);

  const init = async () => {
    const data: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          facet_by: "dump.asset.sub_asset_type_slug",
          facet_return_parent: "dump.asset.sub_asset_type_slug",
          max_facet_values: "500",
          per_page: 0,
        },
      ],
    });
    const filteredPropertiesData = data.results
      .flatMap(
        (result: any) =>
          (result?.facet_counts &&
            result?.facet_counts[0]?.counts.map((facet: any) => {
              return {
                title: facet?.parent?.asset_type_name,
                value: facet?.parent?.asset_type_slug,
                children: [
                  {
                    title: facet?.parent?.sub_asset_type_name,
                    href: `/${facet?.parent?.asset_type_slug}-${facet?.parent?.sub_asset_type_slug}-auction-properties`,
                    value: facet?.parent?.sub_asset_type_slug,
                  },
                ],
              };
            })) ||
          []
      )
      .reduce((acc: NavigationItems[], item: any) => {
        const existingCategory: any = acc.find(
          (cat) => cat.title === item.title
        );
        if (existingCategory) {
          existingCategory.children.push(...item.children);
        } else {
          acc.push(item);
        }
        return acc;
      }, [])
      .sort(
        (a: any, b: any) =>
          (a.value === "others" ? 1 : 0) - (b.value === "others" ? 1 : 0)
      );

    setNavigationItems(
      filteredPropertiesData.length > 0
        ? filteredPropertiesData
        : navigationItemsFallback
    );
  };

  React.useEffect(() => {
    (async () => {
      await init();
    })();
  }, []);

  return (
    <NavigationMenu className="w-full  max-w-full overflow-x-auto scrollbar-hide mobile:max-w-[360px] lg:max-w-max flex justify-between gap-x-6 lg:gap-x-10">
      {navigationItems.map((item) => (
        <NavigationMenuList key={item.value}>
          <NavigationMenuItem className="group">
            <NavigationMenuTrigger
              className={`font-spaceGrotesk font-normal text-[12px] uppercase text-mobile-body-sm lg:text-web-body-md leading-4  p-0 border-b-2 border-b-transparent  lg:border-b-4 hover:border-b-transparent hover:border-b-2 mid:px-2 lg:hover:border-b-4 ${
                item.value === activeItem
                  ? "text-brand-textBlack"
                  : "text-[#A8A8A8]"
              }`}
              onClick={() => {
                searchForm.setField("asset_type", { value: item.value });
                setActiveItem(item.value);
              }}
            >
              {item.title}
              <div
                className={cn(
                  "h-0.5 lg:h-1 w-full absolute bottom-0 left-0 rounded-sm group-hover:bg-brand-orange group-hover:text-brand-textBlack text-brand-textBlack",
                  item.value === activeItem && "bg-brand-orange"
                )}
              />{" "}
            </NavigationMenuTrigger>
          </NavigationMenuItem>
        </NavigationMenuList>
      ))}
    </NavigationMenu>
  );
}
